import { MultiSelectCreatable, MultiSelectOption } from "@/components/misc/Multiselect";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI, { SongForm } from "@/javascript/conduction-api";
import { Artist, Genre } from "@/javascript/library-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useEffect, useState } from "react";
import ConductionModal from "../ConductionModal";

interface SongCreationFormProps {
    onClose: () => void;
    onCreated: () => void;
}

const SongCreationForm: React.FC<SongCreationFormProps> = ({ onClose, onCreated }) => {
    const [genres, setGenres] = useState<Genre[]>([]);
    const [artists, setArtists] = useState<Artist[]>([]);
    const [loadingGenres, setLoadingGenres] = useState<boolean>(true);
    const [loadingArtists, setLoadingArtists] = useState<boolean>(true);

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("Modal must be a child of AuthContext.");
    }
    const token = auth.token;
    if (token === null) {
        return;
    }

    const form = useForm({
        initialValues: {
            songTitle: "",
            selectedGenres: [] as MultiSelectOption[],
            selectedArtists: [] as MultiSelectOption[],
        },
        validate: {
            songTitle: (value) => (value.trim().length === 0 ? "Song title is required" : null),
            selectedGenres: (value) => (value.length === 0 ? "At least one genre must be selected" : null),
            selectedArtists: (value) => (value.length === 0 ? "At least one artist must be selected" : null),
        },
    });

    useEffect(() => {
        const fetchGenres = async () => {
            try {
                const genresData = await ConductionAPI.getGenres();
                setGenres(genresData || []);
            } catch (error) {
                console.error("Error fetching genres", error);
            } finally {
                setLoadingGenres(false);
            }
        };

        const fetchArtists = async () => {
            try {
                const artistsData = await ConductionAPI.getArtists();
                setArtists(artistsData || []);
            } catch (error) {
                console.error("Error fetching artists", error);
            } finally {
                setLoadingArtists(false);
            }
        };

        fetchGenres();
        fetchArtists();
    }, []);

    const handleSubmit = async () => {
        if (!form.isValid()) {
            form.validate();
            return;
        }

        const notificationId = Utils.displayNotification(
            "Creating Song...",
            "Your new song entry is being created.",
            "blue",
            "loading"
        );

        const songForm: SongForm = {
            songName: form.values.songTitle,
            genreIds: form.values.selectedGenres.map((genre) => genre.value.toString()),
            artistIds: form.values.selectedArtists.map((artist) => artist.value.toString()),
        };

        try {
            await ConductionAPI.createNewSongEntry(songForm, token);
            Utils.updateNotification(notificationId, "Song Created", undefined, "green", <IconCheck size={16} />);
            onCreated();
            onClose();
        } catch (error) {
            console.error("Error creating song entry", error);
            Utils.updateNotification(notificationId, "Error", "Failed to create song entry.", "red", <IconAlertCircle size={16} />, true);
        }
    };

    const genreOptions: MultiSelectOption[] = genres.map((genre) => ({
        label: genre.genreName,
        value: genre.genreId,
    }));

    const artistOptions: MultiSelectOption[] = artists.map((artist) => ({
        label: artist.artistName,
        value: artist.artistId,
    }));

    const height = 40; // view height (vh)

    return (
        <ConductionModal
            onClose={onClose}
            title="Create New Song"
            size="lg"
        >
            <Box
                style={{
                    minHeight: `${height}vh`
                }}
            >
                {
                    (loadingGenres || loadingArtists) ? (
                        <Skeleton height={"100%"} radius="md" />
                    ) : (
                        <form onSubmit={form.onSubmit(handleSubmit)}>
                            <Flex
                                direction={"column"}
                                justify={"space-between"}
                                align={"stretch"}
                                style={{
                                    minHeight: `${height}vh`
                                }}
                                onKeyDown={e => e.stopPropagation()} // Stop the key event from propagating to timeline where it is consumed as a hotkey
                                onKeyUp={e => e.stopPropagation()}
                            >
                                <Stack>
                                    <TextInput
                                        label="Title"
                                        {...form.getInputProps("songTitle")}
                                        required
                                    />

                                    <Stack>
                                        <Text fw={500}>Artists</Text>
                                        <MultiSelectCreatable
                                            options={artistOptions}
                                            value={form.values.selectedArtists}
                                            {...form.getInputProps("selectedArtists")}
                                        />
                                    </Stack>

                                    <Stack>
                                        <Text fw={500}>Genres</Text>
                                        <MultiSelectCreatable
                                            options={genreOptions}
                                            value={form.values.selectedGenres}
                                            {...form.getInputProps("selectedGenres")}
                                        />
                                    </Stack>
                                </Stack>

                                <Button
                                    type="submit"
                                    radius="xl"
                                    size='sm'
                                    loading={form.submitting}
                                    color="red"
                                >
                                    Create Song
                                </Button>
                            </Flex>
                        </form>
                    )
                }
            </Box>
        </ConductionModal>
    );
};

export default SongCreationForm;
